/* eslint-disable no-console */
import * as _ from 'lodash';
import * as utils from './utils';
import { getLogger } from './commons/logger';
import type { TestWithCustomTestDataAndRunConfig } from 'services/src/commons/testListBuilder';

const logger = getLogger('override-test-data-builder');

// Override test data can be an object or an array of objects
// Example:
// {
//     "login test": [ { user: "dave", password : "123"}, { "user":"tom"} ]
// }
// Or
// {
//     "login test": {
//         "user": "dave",
//         "password": "123"
//     }
// }

type TestOverrideTestData = { [key: string]: any } | { [key: string]: any }[];
export class OverrideTestDataBuilder {
    constructor(
        private readonly params: any,
        private readonly testInfoList: TestWithCustomTestDataAndRunConfig[],
        private readonly projectId: string,
    ) {}

    private isObjectNotArray(param: any): param is { [key: string]: any } {
        return _.isObject(param) && !Array.isArray(param);
    }

    private isArrayOfObjects(params: any): params is { [key: string]: any }[] {
        return Array.isArray(params) && params.filter(param => !this.isObjectNotArray(param)).length === 0;
    }

    public overrideTestData(overrideAllTestData = false) {
        const { params, projectId } = this;
        if (this.isObjectNotArray(params) && params.overrideTestData !== undefined) {
            if (this.isObjectNotArray(params.overrideTestData) && !_.isEmpty(params.overrideTestData)) {
                Object.keys(params.overrideTestData).forEach(testName => this.overrideSingleTest(testName, params.overrideTestData[testName]));
                delete params.overrideTestData;
            } else {
                logger.error('invalid overrideTestData', { overrideTestData: params.overrideTestData, projectId });
            }
        }
        if (this.isObjectNotArray(params) && params.overrideAllTestsData !== undefined) {
            if (_.isObject(params.overrideAllTestsData) && !_.isEmpty(params.overrideAllTestsData)) {
                const testNames = this.testInfoList.map(test => test.name);
                testNames.forEach(testName => this.overrideSingleTest(testName, params.overrideAllTestsData));
                delete params.overrideAllTestsData;
            } else {
                logger.error('invalid overrideAllTestsData', { overrideAllTestsData: params.overrideAllTestsData, projectId });
            }
        }

        if (this.isArrayOfObjects(params) && overrideAllTestData) {
            const testNames = this.testInfoList.map(test => test.name);
            testNames.forEach(testName => this.overrideSingleTest(testName, params));
        }

        return this.testInfoList;
    }

    private overrideSingleTest(testName: string, testOverrideTestData: any) {
        const { projectId } = this;
        if (this.isObjectNotArray(testOverrideTestData) || this.isArrayOfObjects(testOverrideTestData)) {
            this.replaceAndCreateOverrideTestData(testName, testOverrideTestData);
            return;
        }
        logger.error('skip override test data to test name', { testName, projectId });
        console.error(`Invalid override test data provided to test '${testName}'`);
    }

    private replaceAndCreateOverrideTestData(testName: string, testOverrideTestData: TestOverrideTestData) {
        const uniqueMatchIds = this.mapTestListToUniqueId(testName);
        if (uniqueMatchIds.length === 0) {
            return undefined;
        }
        return this.createNewTestPerOverrideTestData(uniqueMatchIds, testOverrideTestData);
    }

    private mapTestListToUniqueId(testName: string) {
        const { testInfoList } = this;
        return _.chain(testInfoList).map(test => {
            if (test.name.toLowerCase() === testName.toLowerCase()) {
                return this.generateTestUniqId(test);
            }
            return undefined;
        }).compact().uniq()
            .value();
    }

    private createNewTestPerOverrideTestData(uniqueMatchIds: string[], testOverrideTestData: TestOverrideTestData) {
        const { testInfoList } = this;
        return [...new Set(uniqueMatchIds)].map(uniqTestId => {
            const testUniqIds = testInfoList.map(test => this.generateTestUniqId(test));
            const firstIndex = testUniqIds.indexOf(uniqTestId);
            const lastIndex = testUniqIds.lastIndexOf(uniqTestId);
            const currentTest = testInfoList[firstIndex];
            const newTestDataItems = this.createNewTestItems(currentTest, testOverrideTestData);
            return testInfoList.splice(firstIndex, (lastIndex - firstIndex) + 1, ...newTestDataItems);
        });
    }

    private createNewTestItems(currentTest: TestWithCustomTestDataAndRunConfig, testOverrideTestData: TestOverrideTestData) {
        return (([] as { [key: string]: any }[]).concat(testOverrideTestData)).map((newTestData, index) => {
            const testResultId = utils.guid();
            return {
                ...currentTest,
                resultId: testResultId,
                testData: {
                    value: newTestData,
                    index: index + 1,
                    total: testOverrideTestData.length || 1,
                },
            } as TestWithCustomTestDataAndRunConfig;
        });
    }

    getTestType(test: TestWithCustomTestDataAndRunConfig & { isBeforeTestPlan?: true; isAfterTestPlan?: true }) {
        if (test.isBeforeTestPlan) {
            return 'before';
        }
        if (test.isAfterTestPlan) {
            return 'after';
        }
        return 'test';
    }

    generateTestUniqId(test: TestWithCustomTestDataAndRunConfig) {
        return `${test.testId}:${test.testConfig.id}:${this.getTestType(test)}`;
    }
}
